/**
 * App.tsx — Main Application Component
 * 
 * Orchestrates the layout: Header, Footer, CustomCursor, 
 * ScrollProgressBar (customizable), and SettingsPanel.
 * 
 * All routes are registered here as a central routing hub.
 */

import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Home } from './pages/Home';
import { AboutMe } from './pages/AboutMe';
import { Games } from './pages/Games';
import { Gallery } from './pages/Gallery';
import { ContactPage } from './pages/ContactPage';
import { Dashboard } from './pages/Dashboard';
import { Timeline } from './pages/Timeline';
import { Architecture } from './pages/Architecture';
import { Achievements } from './pages/Achievements';
import { Nexus } from './pages/Nexus';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { CustomCursor } from './components/layout/CustomCursor';
import { ScrollProgressBar } from './components/ui/ScrollProgressBar';
import { SettingsPanel } from './components/ui/SettingsPanel';
import type { ProgressBarStyle, ProgressBarColor } from './components/ui/SettingsPanel';

function Layout() {
    const location = useLocation();

    // UI customization state — persisted in localStorage
    const [progressStyle, setProgressStyle] = useState<ProgressBarStyle>(() => {
        return (localStorage.getItem('progressStyle') as ProgressBarStyle) || 'lightning';
    });
    const [progressColor, setProgressColor] = useState<ProgressBarColor>(() => {
        return (localStorage.getItem('progressColor') as ProgressBarColor) || 'neon-emerald';
    });

    // Persist settings to localStorage so they survive page refreshes
    const handleStyleChange = (style: ProgressBarStyle) => {
        setProgressStyle(style);
        localStorage.setItem('progressStyle', style);
    };

    const handleColorChange = (color: ProgressBarColor) => {
        setProgressColor(color);
        localStorage.setItem('progressColor', color);
    };

    // Scroll to top on route change
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location]);

    return (
        <div className="bg-tech-bg min-h-screen text-white selection:bg-emerald-500/30 selection:text-emerald-200 overflow-x-hidden">
            <CustomCursor />
            <Header />

            {/* Customizable Scroll Progress Bar */}
            <ScrollProgressBar style={progressStyle} color={progressColor} />

            {/* Settings Panel — fixed bottom-right gear icon */}
            <SettingsPanel
                progressStyle={progressStyle}
                progressColor={progressColor}
                onStyleChange={handleStyleChange}
                onColorChange={handleColorChange}
            />

            <main className="relative z-10 min-h-screen">
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/aboutme" element={<AboutMe />} />
                    <Route path="/gallery" element={<Gallery />} />
                    <Route path="/games" element={<Games />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/timeline" element={<Timeline />} />
                    <Route path="/architecture" element={<Architecture />} />
                    <Route path="/achievements" element={<Achievements />} />
                    <Route path="/nexus" element={<Nexus />} />
                </Routes>
            </main>

            <Footer />
        </div>
    );
}

function App() {
    // Smooth scroll behavior fix for anchors
    useEffect(() => {
        document.documentElement.style.scrollBehavior = 'smooth';
        return () => {
            document.documentElement.style.scrollBehavior = 'auto';
        };
    }, []);

    return (
        <Router>
            <Layout />
        </Router>
    );
}

export default App;
